import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Charts drawn by hand, in SVG.
 *
 * No charting library, because the repo has none and one would be ~100KB to draw
 * three shapes. These are deliberately small and dumb: they take numbers and
 * render them, hold no state, fetch nothing, and are the same size whatever the
 * data — a chart that resizes as figures load is the thing that makes a page
 * jump while somebody is reading it.
 *
 * Every one degrades to a sentence rather than an empty box when there is
 * nothing to show. "No revenue in this period" is information; a blank rectangle
 * is a bug report waiting to be filed.
 */

const money = (n, currency = 'USD') => {
    const v = Number(n) || 0;
    try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(v);
    } catch { return `$${v.toFixed(0)}`; }
};

/**
 * The same figure, with its cents.
 *
 * `money` drops them on purpose — a chart of thousands reads better whole, and
 * nobody reconciles a pie slice. A ledger is the opposite: rounding $31.35 to
 * $31 hides 35c on every row and leaves a balance column that visibly fails to
 * add up, and a $0.30 flat fee vanishes altogether as "$0". Anywhere a reader
 * might check the arithmetic, use this one.
 */
const moneyExact = (n, currency = 'USD') => {
    const v = Number(n) || 0;
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
        }).format(v);
    } catch { return `$${v.toFixed(2)}`; }
};

/** Fixed palette, indexed by position, so a slice keeps its colour between
 *  renders and the legend can be read once rather than re-learned. */
export const SERIES_COLOURS = [
    '#3b82f6', '#f59e0b', '#10b981', '#a855f7', '#ef4444', '#14b8a6', '#eab308', '#6366f1',
];

/**
 * A pie, as SVG arcs.
 *
 * Uses a donut rather than a full circle: the middle carries the total, which is
 * the number people actually want, and it saves a separate heading for it.
 */
export function Pie({ data, size = 190, title }) {
    const { theme } = useTheme();
    const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0);
    if (!total) {
        return (
            <div className={`flex items-center justify-center text-sm ${theme.textMuted}`} style={{ height: size }}>
                Nothing to chart in this period.
            </div>
        );
    }

    const r = size / 2 - 4;
    const c = size / 2;
    let angle = -Math.PI / 2;                       // start at 12 o'clock

    const arcs = data.map((d, i) => {
        const share = (Number(d.value) || 0) / total;
        const sweep = share * Math.PI * 2;
        const x1 = c + r * Math.cos(angle);
        const y1 = c + r * Math.sin(angle);
        angle += sweep;
        const x2 = c + r * Math.cos(angle);
        const y2 = c + r * Math.sin(angle);
        // A single slice covering everything cannot be drawn as an arc — the
        // start and end points coincide and the path collapses to nothing.
        const path = share >= 0.999
            ? `M ${c} ${c - r} A ${r} ${r} 0 1 1 ${c - 0.01} ${c - r} Z`
            : `M ${c} ${c} L ${x1} ${y1} A ${r} ${r} 0 ${sweep > Math.PI ? 1 : 0} 1 ${x2} ${y2} Z`;
        return { path, colour: SERIES_COLOURS[i % SERIES_COLOURS.length], ...d, share };
    });

    return (
        <div className="flex items-center gap-5 flex-wrap">
            <svg width={size} height={size} role="img" aria-label={title || 'Revenue by share'}>
                {arcs.map((a, i) => <path key={i} d={a.path} fill={a.colour} />)}
                <circle cx={c} cy={c} r={r * 0.58} className="fill-white dark:fill-slate-900" />
                <text x={c} y={c - 2} textAnchor="middle" className="fill-current text-sm font-semibold">
                    {money(total)}
                </text>
                <text x={c} y={c + 14} textAnchor="middle" className="fill-current text-[10px] opacity-60">
                    collected
                </text>
            </svg>

            <ul className="text-sm space-y-1 min-w-[9rem]">
                {arcs.map((a, i) => (
                    <li key={i} className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: a.colour }} />
                        <span className={`${theme.text} truncate`}>{a.label}</span>
                        <span className={`${theme.textMuted} ml-auto tabular-nums`}>
                            {money(a.value)} · {Math.round(a.share * 100)}%
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * Horizontal bars — for offerings, methods, anything with a name and a number.
 *
 * Horizontal rather than vertical because the labels are names ("Pickleball
 * Adult Program"), and vertical bars would either truncate them or turn them
 * sideways.
 */
/**
 * `onPick` makes the bars a way in rather than only a picture. Left off, they
 * stay exactly as they were — no cursor, no highlight, nothing to click — so
 * every other chart on the page is untouched by this.
 */
export function Bars({ data, max, valueLabel = (v) => money(v), onPick, picked }) {
    const { theme } = useTheme();
    if (!data.length) {
        return <p className={`text-sm ${theme.textMuted} py-6 text-center`}>Nothing to show.</p>;
    }
    const top = max || Math.max(...data.map(d => Number(d.value) || 0), 1);

    return (
        <ul className="space-y-2">
            {data.map((d, i) => (
                <li
                    key={i}
                    onClick={onPick ? () => onPick(d) : undefined}
                    className={onPick
                        ? `cursor-pointer rounded px-1 -mx-1 py-0.5 transition-colors ${
                            picked === d.label ? theme.backgroundSecondary : theme.cardHover}`
                        : undefined}
                >
                    <div className="flex justify-between text-xs mb-0.5">
                        <span className={`${theme.text} truncate pr-2`}>{d.label}</span>
                        <span className={`${theme.textMuted} tabular-nums flex-shrink-0`}>{valueLabel(d.value)}</span>
                    </div>
                    <div className={`h-2 rounded-full ${theme.backgroundSecondary} overflow-hidden`}>
                        <div
                            className="h-full rounded-full transition-[width]"
                            style={{
                                width: `${Math.max(2, ((Number(d.value) || 0) / top) * 100)}%`,
                                background: SERIES_COLOURS[i % SERIES_COLOURS.length],
                            }}
                        />
                    </div>
                </li>
            ))}
        </ul>
    );
}

/**
 * Hour × weekday heatmap: which court, what time.
 *
 * Reads the session's OWN clock, not the payment date — "Tuesday nights earn
 * well" is a fact about when people play, and dating it by when somebody's card
 * cleared would put a Tuesday session in Thursday's column.
 */
export function Heatmap({ cells, hours = [], days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] }) {
    const { theme } = useTheme();
    const values = Object.values(cells || {}).map(Number).filter(Boolean);
    if (!values.length) {
        return <p className={`text-sm ${theme.textMuted} py-6 text-center`}>No sessions with revenue yet.</p>;
    }
    const max = Math.max(...values);
    const shown = hours.length ? hours : [...new Set(Object.keys(cells).map(k => k.split('|')[1]))].sort();

    return (
        <div className="overflow-x-auto">
            <table className="text-[10px] border-separate" style={{ borderSpacing: 2 }}>
                <thead>
                    <tr>
                        <th />
                        {shown.map(h => <th key={h} className={`${theme.textMuted} font-normal px-1`}>{h}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {days.map(day => (
                        <tr key={day}>
                            <td className={`${theme.textMuted} pr-1 text-right`}>{day}</td>
                            {shown.map(h => {
                                const v = Number(cells[`${day}|${h}`] || 0);
                                return (
                                    <td key={h}
                                        title={v ? `${day} ${h}:00 — ${money(v)}` : `${day} ${h}:00 — nothing`}
                                        className="w-6 h-6 rounded"
                                        style={{
                                            background: v ? `rgba(59,130,246,${0.15 + 0.85 * (v / max)})` : 'rgba(127,127,127,0.10)',
                                        }}
                                    />
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export { money, moneyExact };
